import { useEffect, useMemo, useState } from 'react';
import PicklistApi from '../../../api/PicklistApi';
import {
  isPicklistGroupField,
  normalizePicklistValuesArray,
  resolvePicklistValuesForGroupField,
} from '../charts/chartPicklistColors';

function normalizeApiPicklistRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    value_key: row.value_key ?? row.valueKey ?? row.key,
    value_label: row.value_label ?? row.valueLabel ?? row.value ?? row.label,
    key: row.value_key ?? row.valueKey ?? row.key,
    value: row.value_label ?? row.valueLabel ?? row.value,
    label: row.value_label ?? row.valueLabel ?? row.label,
    sort_order: row.sort_order ?? row.sortOrder ?? 0,
    is_active: row.is_active ?? row.isActive ?? true,
    path_color: row.path_color ?? row.pathColor,
  };
}

/**
 * Load active picklist values for report group fields (same source as PicklistValueManager).
 * @returns {Record<string, object[]>} fieldId → picklist rows sorted by sort_order
 */
function stableGroupFieldsKey(groupFields) {
  return (groupFields || [])
    .map(
      (f) =>
        `${f.id || f.field_config_id || f.field_id || ''}:${f.field_name || ''}:${f.object_name || 'main'}`
    )
    .join('|');
}

function picklistMapsEqual(a, b) {
  const left = a || {};
  const right = b || {};
  const keysA = Object.keys(left);
  const keysB = Object.keys(right);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => left[key] === right[key]);
}

export function useReportGroupPicklistValues(groupFields = [], reportFields = []) {
  const [picklistValuesByFieldId, setPicklistValuesByFieldId] = useState({});
  const groupFieldsKey = useMemo(
    () => stableGroupFieldsKey(groupFields),
    [groupFields]
  );
  const reportFieldsKey = useMemo(
    () =>
      (reportFields || [])
        .map((f) => `${f.id || f.field_config_id || ''}:${f.field_name || ''}`)
        .join('|'),
    [reportFields]
  );

  useEffect(() => {
    let cancelled = false;
    const picklistFields = (groupFields || []).filter(isPicklistGroupField);
    if (!picklistFields.length) {
      setPicklistValuesByFieldId((prev) =>
        Object.keys(prev).length === 0 ? prev : {}
      );
      return undefined;
    }

    const load = async () => {
      const api = new PicklistApi();
      const next = {};

      for (const field of picklistFields) {
        const fieldId = field.id || field.field_config_id || field.field_id;
        if (!fieldId) continue;

        const embedded = resolvePicklistValuesForGroupField(field, reportFields);
        if (embedded.length > 0) {
          next[String(fieldId)] = embedded;
          continue;
        }

        try {
          const res = await api.getActivePicklistValues(fieldId);
          const raw = res?.data?.data ?? res?.data;
          if (Array.isArray(raw) && raw.length > 0) {
            next[String(fieldId)] = raw
              .map(normalizeApiPicklistRow)
              .filter(Boolean)
              .sort(
                (a, b) =>
                  (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
                  String(a.value_label || '').localeCompare(
                    String(b.value_label || ''),
                    undefined,
                    { sensitivity: 'base' }
                  )
              );
          }
        } catch (err) {
          console.warn(
            '[reports] Failed to load picklist values for field',
            field.field_name,
            err
          );
        }
      }

      if (!cancelled) {
        setPicklistValuesByFieldId((prev) =>
          picklistMapsEqual(prev, next) ? prev : next
        );
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [groupFieldsKey, reportFieldsKey]);

  return picklistValuesByFieldId;
}

export function mapReportFieldFromApi(field) {
  const picklistValues =
    normalizePicklistValuesArray(field.picklist_values_data) ||
    normalizePicklistValuesArray(field.picklist_values) ||
    normalizePicklistValuesArray(field.picklistValues);

  return {
    id: field.field_config_id || field.id,
    field_config_id: field.field_config_id,
    field_id: field.field_config_id,
    field_name: field.field_name,
    field_label: field.field_label,
    data_type: field.data_type,
    field_type: field.field_type,
    object_name: field.object_name,
    is_aggregated: field.is_aggregated,
    aggregate_function: field.aggregate_function,
    is_grouped: field.is_grouped,
    group_axis: field.group_axis,
    sort_order: field.sort_order,
    display_config: field.display_config,
    displayConfig: field.display_config,
    config: field.config,
    fieldConfig: field.config,
    scale: field.scale,
    rollup_field: field.rollup_field,
    picklist_values: picklistValues,
    picklist_values_data: picklistValues,
  };
}
